import { toURLSearchParams } from '@wix/sdk-runtime/rest-modules';
import { transformSDKFieldMaskToRESTFieldMask } from '@wix/sdk-runtime/transformations/field-mask';
import { transformPaths } from '@wix/sdk-runtime/transformations/transform-paths';
import { resolveUrl } from '@wix/sdk-runtime/rest-modules';
import { ResolveUrlOpts } from '@wix/sdk-runtime/rest-modules';
import { RequestOptionsFactory } from '@wix/sdk-types';

function resolveWixPromoteSeoMetatagsV1PageTypeSeoPatternsServiceUrl(
  opts: Omit<ResolveUrlOpts, 'domainToMappings'>
) {
  const domainToMappings = {
    'www.wixapis.com': [
      {
        srcPath: '/seo-metatags-server',
        destPath: '',
      },
      {
        srcPath: '/promote/seo/v1/item-seo-tags',
        destPath: '/v1/item-seo-tags',
      },
      {
        srcPath: '/promote/seo/v1/site-seo-tags',
        destPath: '/v1/site-seo-tags',
      },
      {
        srcPath: '/promote/seo/v1/seo-patterns',
        destPath: '/v1/seo-patterns',
      },
      {
        srcPath: '/promote/seo/v1/bulk/item-seo-tags',
        destPath: '/v1/bulk/item-seo-tags',
      },
    ],
    'manage._base_domain_': [
      {
        srcPath: '/_api/seo-metatags-server',
        destPath: '',
      },
    ],
    '*.dev.wix-code.com': [
      {
        srcPath: '/promote/seo/v1/item-seo-tags',
        destPath: '/v1/item-seo-tags',
      },
      {
        srcPath: '/promote/seo/v1/site-seo-tags',
        destPath: '/v1/site-seo-tags',
      },
      {
        srcPath: '/promote/seo/v1/seo-patterns',
        destPath: '/v1/seo-patterns',
      },
      {
        srcPath: '/promote/seo/v1/bulk/item-seo-tags',
        destPath: '/v1/bulk/item-seo-tags',
      },
    ],
    _: [
      {
        srcPath: '/promote/seo/v1/item-seo-tags',
        destPath: '/v1/item-seo-tags',
      },
      {
        srcPath: '/promote/seo/v1/site-seo-tags',
        destPath: '/v1/site-seo-tags',
      },
      {
        srcPath: '/promote/seo/v1/seo-patterns',
        destPath: '/v1/seo-patterns',
      },
      {
        srcPath: '/promote/seo/v1/bulk/item-seo-tags',
        destPath: '/v1/bulk/item-seo-tags',
      },
    ],
  };

  return resolveUrl(Object.assign(opts, { domainToMappings }));
}

const PACKAGE_NAME = '@wix/auto_sdk_seo_seo-patterns';

/**
 * Retrieves the SEO pattern in effect for a page type.
 *
 * The response contains the site's own pattern in `pattern`, and the pattern Wix
 * provides in `defaultPattern`. `source` reports which of the two is in effect.
 * `pattern` is empty when the site has no pattern of its own.
 */
export function getSeoPattern(payload: object): RequestOptionsFactory<any> {
  function __getSeoPattern({ host }: any) {
    const metadata = {
      entityFqdn: 'wix.promote.seo.v1.seo_pattern',
      method: 'GET' as any,
      methodFqn:
        'wix.promote.seo.metatags.v1.PageTypeSeoPatternsService.GetSeoPattern',
      packageName: PACKAGE_NAME,
      migrationOptions: {
        optInTransformResponse: true,
      },
      url: resolveWixPromoteSeoMetatagsV1PageTypeSeoPatternsServiceUrl({
        protoPath: '/v1/seo-patterns/{pageType}',
        data: payload,
        host,
      }),
      params: toURLSearchParams(payload),
    };

    return metadata;
  }

  return __getSeoPattern;
}

/**
 * Retrieves the patterns stored for a site, one per page type.
 *
 * Only returns page types that have had a pattern of the site's own stored, so a page
 * type that has never had one is absent. A page type that was reset to its default remains in the
 * list, with an empty `pattern` and `source` of `PATTERN_SOURCE_DEFAULT`.
 */
export function listSeoPatterns(payload: object): RequestOptionsFactory<any> {
  function __listSeoPatterns({ host }: any) {
    const metadata = {
      entityFqdn: 'wix.promote.seo.v1.seo_pattern',
      method: 'GET' as any,
      methodFqn:
        'wix.promote.seo.metatags.v1.PageTypeSeoPatternsService.ListSeoPatterns',
      packageName: PACKAGE_NAME,
      migrationOptions: {
        optInTransformResponse: true,
      },
      url: resolveWixPromoteSeoMetatagsV1PageTypeSeoPatternsServiceUrl({
        protoPath: '/v1/seo-patterns',
        data: payload,
        host,
      }),
      params: toURLSearchParams(payload),
    };

    return metadata;
  }

  return __listSeoPatterns;
}

/**
 * Retrieves the variables a pattern for a page type may reference.
 *
 * Call this method to retrieve the valid variables for a page type before writing a
 * pattern.
 *
 * Variables for the page's own tags and variables for its structured data are returned
 * in separate fields, and a pattern may use either.
 */
export function listSeoPatternVariables(
  payload: object
): RequestOptionsFactory<any> {
  function __listSeoPatternVariables({ host }: any) {
    const metadata = {
      entityFqdn: 'wix.promote.seo.v1.seo_pattern',
      method: 'GET' as any,
      methodFqn:
        'wix.promote.seo.metatags.v1.PageTypeSeoPatternsService.ListSeoPatternVariables',
      packageName: PACKAGE_NAME,
      migrationOptions: {
        optInTransformResponse: true,
      },
      url: resolveWixPromoteSeoMetatagsV1PageTypeSeoPatternsServiceUrl({
        protoPath: '/v1/seo-patterns/{pageType}/variables',
        data: payload,
        host,
      }),
      params: toURLSearchParams(payload),
    };

    return metadata;
  }

  return __listSeoPatternVariables;
}

/**
 * Creates a pattern for a page type that's still on its Wix default.
 *
 * The pattern must contain at least one tag. To leave a page type on its Wix default,
 * create nothing for it.
 *
 * To change a pattern that already exists, call [Set SEO
 * Pattern](https://dev.wix.com/docs/api-reference/business-management/seo/seo-pattern-v1/set-seo-pattern).
 */
export function createSeoPattern(payload: object): RequestOptionsFactory<any> {
  function __createSeoPattern({ host }: any) {
    const metadata = {
      entityFqdn: 'wix.promote.seo.v1.seo_pattern',
      method: 'POST' as any,
      methodFqn:
        'wix.promote.seo.metatags.v1.PageTypeSeoPatternsService.CreateSeoPattern',
      packageName: PACKAGE_NAME,
      migrationOptions: {
        optInTransformResponse: true,
      },
      url: resolveWixPromoteSeoMetatagsV1PageTypeSeoPatternsServiceUrl({
        protoPath: '/v1/seo-patterns/{pageType}',
        data: payload,
        host,
      }),
      data: payload,
    };

    return metadata;
  }

  return __createSeoPattern;
}

/**
 * Changes the pattern a site has for a page type.
 *
 * Setting `pattern` replaces the page type's tag templates in full, so retrieve the
 * pattern first and send back the complete set you want it to have. Sending an empty
 * pattern clears it, which returns the page type to its Wix default.
 *
 * This method changes a pattern that already exists rather than creating one. For a page
 * type still on its Wix default, call [Create SEO
 * Pattern](https://dev.wix.com/docs/api-reference/business-management/seo/seo-pattern-v1/create-seo-pattern)
 * instead.
 *
 * A pattern has no revision, and the site's SEO settings in the dashboard write to the
 * same pattern. The last write wins, so retrieve the pattern immediately before writing
 * it.
 */
export function setSeoPattern(payload: object): RequestOptionsFactory<any> {
  function __setSeoPattern({ host }: any) {
    const serializedData = transformPaths(payload, [
      {
        transformFn: transformSDKFieldMaskToRESTFieldMask,
        paths: [{ path: 'fieldMask' }],
      },
    ]);
    const metadata = {
      entityFqdn: 'wix.promote.seo.v1.seo_pattern',
      method: 'PATCH' as any,
      methodFqn:
        'wix.promote.seo.metatags.v1.PageTypeSeoPatternsService.SetSeoPattern',
      packageName: PACKAGE_NAME,
      migrationOptions: {
        optInTransformResponse: true,
      },
      url: resolveWixPromoteSeoMetatagsV1PageTypeSeoPatternsServiceUrl({
        protoPath: '/v1/seo-patterns/{pageType}',
        data: serializedData,
        host,
      }),
      data: serializedData,
    };

    return metadata;
  }

  return __setSeoPattern;
}

/**
 * Removes the pattern a site has for a page type, so the Wix default applies again.
 *
 * The response reports the page type after the reset, with an empty `pattern` and
 * `source` of `PATTERN_SOURCE_DEFAULT`.
 */
export function resetSeoPatternToDefault(
  payload: object
): RequestOptionsFactory<any> {
  function __resetSeoPatternToDefault({ host }: any) {
    const metadata = {
      entityFqdn: 'wix.promote.seo.v1.seo_pattern',
      method: 'POST' as any,
      methodFqn:
        'wix.promote.seo.metatags.v1.PageTypeSeoPatternsService.ResetSeoPatternToDefault',
      packageName: PACKAGE_NAME,
      migrationOptions: {
        optInTransformResponse: true,
      },
      url: resolveWixPromoteSeoMetatagsV1PageTypeSeoPatternsServiceUrl({
        protoPath: '/v1/seo-patterns/{pageType}/reset-to-default',
        data: payload,
        host,
      }),
      data: payload,
    };

    return metadata;
  }

  return __resetSeoPatternToDefault;
}
